import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Activity,
  Bot,
  Circle,
  Search,
  Play,
  Pause,
  Settings,
  TrendingUp
} from 'lucide-react';
import { mockAIAgents } from '../mock/mockData';

const AIAgentsModule = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAgents = mockAIAgents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category) => {
    const colors = {
      compliance: 'bg-blue-100 text-blue-700',
      tax: 'bg-green-100 text-green-700',
      audit: 'bg-purple-100 text-purple-700',
      sales: 'bg-orange-100 text-orange-700',
      operations: 'bg-cyan-100 text-cyan-700',
      risk: 'bg-red-100 text-red-700',
      communication: 'bg-pink-100 text-pink-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const totalActive = mockAIAgents.filter((a) => a.status === 'active').length;
  const totalTasks = mockAIAgents.reduce((sum, agent) => sum + agent.tasks, 0);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Agency Module - 185 AI Agents
        </h1>
        <p className="text-gray-600">Manage and monitor your autonomous AI agent workforce</p>
      </div>

      <div className="p-8 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Bot className="w-8 h-8 text-blue-600" />
                <Badge variant="secondary">100%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">185</p>
              <p className="text-sm text-gray-600 mt-1">Total Agents</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Activity className="w-8 h-8 text-green-600" />
                <Badge className="bg-green-100 text-green-700">{totalActive} Active</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{totalActive}</p>
              <p className="text-sm text-gray-600 mt-1">Active Now</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                <Badge variant="secondary">+23%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{totalTasks}</p>
              <p className="text-sm text-gray-600 mt-1">Tasks Processed</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Circle className="w-8 h-8 text-orange-600" />
                <Badge className="bg-orange-100 text-orange-700">98.7%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">98.7%</p>
              <p className="text-sm text-gray-600 mt-1">Success Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Agent Directory</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search agents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAgents.map((agent) => (
                <Card
                  key={agent.id}
                  className="hover:shadow-lg transition-all border-2 hover:border-blue-300"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Circle
                          className={`w-2 h-2 ${
                            agent.status === 'active'
                              ? 'fill-green-500 text-green-500'
                              : 'fill-gray-400 text-gray-400'
                          }`}
                        />
                        <span
                          className={`text-xs font-medium ${
                            agent.status === 'active' ? 'text-green-700' : 'text-gray-500'
                          }`}
                        >
                          {agent.status}
                        </span>
                      </div>
                    </div>

                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {agent.name}
                    </h3>
                    <Badge className={getCategoryColor(agent.category)}>
                      {agent.category}
                    </Badge>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Tasks Completed</span>
                        <span className="font-semibold text-gray-900">{agent.tasks}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-2">
                      <Button size="sm" variant="outline" className="flex-1">
                        {agent.status === 'active' ? (
                          <>
                            <Pause className="w-3 h-3 mr-1" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3 mr-1" />
                            Start
                          </>
                        )}
                      </Button>
                      <Button size="sm" variant="outline">
                        <Settings className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination placeholder */}
            <div className="mt-6 flex items-center justify-center">
              <p className="text-sm text-gray-500">
                Showing {filteredAgents.length} of 185 agents
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIAgentsModule;
